import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { GamePopup } from '../../components/gamePopup';
import { CircleOutput } from '../../components/circleOutput';
import { useGameLaunch } from '../../hooks/useGameLaunch';
import { useLauncherGameLocale } from '../../hooks/useLauncherLocale';
import { gamesService, type Game as GameModel } from '../../services/games';
import {
    gameLanguagesService,
    type GameLanguage,
} from '../../services/gameLanguages';
import { GameBanner } from './components/gameBanner';
import { GameParameters } from './components/gameParameters';
import type { GamePageLocationState } from './types';
import {
    buildGameFeatureItems,
    formatMaxMultiplier,
    formatVolatility,
    resolveBannerDescription,
} from './utils';

import styles from './GameDetails.module.css';

export function GameDetails() {
    const { gameId } = useParams<{ gameId: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const locale = useLauncherGameLocale();
    const { activeGame, handleLaunch, handleClose } = useGameLaunch({ useGameLocale: true });
    const [game, setGame] = useState<GameModel | null>(null);
    const [gameLanguage, setGameLanguage] = useState<GameLanguage | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!gameId) {
            setError('Game not found');
            setGame(null);
            setIsLoading(false);
            return;
        }

        let isMounted = true;
        setIsLoading(true);

        gamesService
            .getLiveGame(gameId)
            .then((loadedGame) => {
                if (!isMounted) {
                    return;
                }

                setGame(loadedGame);
                setError(null);
            })
            .catch((loadError) => {
                if (isMounted) {
                    setError(loadError instanceof Error ? loadError.message : 'Failed to load game');
                    setGame(null);
                }
            })
            .finally(() => {
                if (isMounted) {
                    setIsLoading(false);
                }
            });

        return () => {
            isMounted = false;
        };
    }, [gameId]);

    useEffect(() => {
        if (!gameId) {
            setGameLanguage(null);
            return;
        }

        let isMounted = true;

        gameLanguagesService
            .getLiveGameLanguage(gameId, locale)
            .then((loadedGameLanguage) => {
                if (isMounted) {
                    setGameLanguage(loadedGameLanguage);
                }
            })
            .catch(() => {
                if (isMounted) {
                    setGameLanguage(null);
                }
            });

        return () => {
            isMounted = false;
        };
    }, [gameId, locale]);

    const handleBack = () => {
        const returnTo = (location.state as GamePageLocationState | null)?.returnTo;
        if (returnTo) {
            navigate(returnTo);
            return;
        }
        navigate(-1);
    };

    if (isLoading) {
        return <p className={styles.status}>Loading game...</p>;
    }

    if (error || !game) {
        return <p className={styles.status}>{error ?? 'Game not found'}</p>;
    }

    const gameStory = gameLanguage?.gameStory?.trim() || null;
    const featureItems = buildGameFeatureItems(game, gameLanguage);

    return (
        <div className={styles.page}>
            <GameBanner
                game={game}
                description={resolveBannerDescription(gameLanguage)}
                onBack={handleBack}
                onPlay={() => handleLaunch(game)}
            />

            <div className={styles.content}>
                <div className={styles.detailsStoryRow}>
                    <section className={styles.section}>
                        <h2 className={styles.sectionTitle}>Game Details</h2>
                        <div className={styles.circleOutputs}>
                            <CircleOutput
                                title="Max Multiplier"
                                value={formatMaxMultiplier(game.parameters.maxMultiplier)}
                            />
                            <CircleOutput title="RTP" value={game.parameters.rtp?.trim() || null} />
                            <CircleOutput
                                title="Volatility"
                                value={formatVolatility(game.parameters.volatility)}
                            />
                        </div>
                        <GameParameters parameters={game.parameters} gameLanguage={gameLanguage} />
                    </section>

                    <section className={styles.section}>
                        <h2 className={styles.sectionTitle}>Game Story</h2>
                        {gameStory ? <p className={styles.storyText}>{gameStory}</p> : null}
                    </section>
                </div>

                {featureItems.length > 0 ? (
                    <section className={styles.featuresSection}>
                        <h2 className={styles.sectionTitle}>Game Features</h2>
                        <div className={styles.featuresGrid}>
                            {featureItems.map((feature, index) => (
                                <figure
                                    key={`gameFeature_${index}`}
                                    className={`${styles.featuresCard}${feature.featured ? ` ${styles.featuresCardFeatured}` : ''}`}
                                >
                                    <img
                                        src={feature.src}
                                        alt=""
                                        className={styles.featuresImage}
                                    />
                                    {feature.title || feature.text ? (
                                        <figcaption className={styles.featuresCaption}>
                                            {feature.title ? (
                                                <h3 className={styles.featuresCaptionTitle}>
                                                    {feature.title}
                                                </h3>
                                            ) : null}
                                            {feature.text ? (
                                                <p className={styles.featuresCaptionText}>
                                                    {feature.text}
                                                </p>
                                            ) : null}
                                        </figcaption>
                                    ) : null}
                                </figure>
                            ))}
                        </div>
                    </section>
                ) : null}
            </div>

            {activeGame ? (
                <GamePopup
                    url={activeGame.url}
                    name={activeGame.name}
                    format={activeGame.format}
                    onClose={handleClose}
                />
            ) : null}
        </div>
    );
}
